// widget/src/components/Composer.tsx
//
// The pill-shaped composer at the bottom of the active stack. Hidden
// while idle (the parent host carries the `active` state class). The
// voice orb is NOT rendered here — it sits next to the composer in the
// parent's `.orb-row`.

import { useEffect, useRef, useState, type KeyboardEvent } from "react"
import styles from "../styles/widget.module.css"

interface Props {
  onSubmit: (text: string) => void
  disabled?: boolean
}

// Desktop carries the longer placeholder because the orb is right there
// next to it and "hold the orb" is a discoverable affordance. On mobile
// the orb is hidden in the active sheet (composer is the only input),
// so the long placeholder is both incorrect and visually cramped.
const PLACEHOLDER_DESKTOP = "Type your message…"
const PLACEHOLDER_MOBILE = "Message Pearl"

function useMobilePlaceholder(): string {
  const [text, setText] = useState(PLACEHOLDER_DESKTOP)
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return
    const mq = window.matchMedia("(max-width: 767px)")
    const apply = () =>
      setText(mq.matches ? PLACEHOLDER_MOBILE : PLACEHOLDER_DESKTOP)
    apply()
    mq.addEventListener("change", apply)
    return () => mq.removeEventListener("change", apply)
  }, [])
  return text
}

export function Composer({ onSubmit, disabled }: Props) {
  const [text, setText] = useState("")
  const placeholder = useMobilePlaceholder()
  const taRef = useRef<HTMLTextAreaElement>(null)

  // Kill any focus outline/border the host storefront's CSS bleeds onto the
  // textarea. The widget has no shadow root, so a Shopify theme rule like
  // `textarea:focus { outline: 2px solid !important }` can out-specify our
  // stylesheet resets. Inline !important set on the element sits at the top
  // of the cascade and beats any author stylesheet regardless of specificity.
  useEffect(() => {
    const ta = taRef.current
    if (!ta) return
    ta.style.setProperty("outline", "none", "important")
    ta.style.setProperty("border", "none", "important")
    ta.style.setProperty("box-shadow", "none", "important")
    ta.style.setProperty("-webkit-appearance", "none", "important")
    ta.style.setProperty("-webkit-tap-highlight-color", "transparent", "important")
  }, [])

  function autoResize() {
    const ta = taRef.current
    if (!ta) return
    ta.style.height = "auto"
    ta.style.height = Math.min(ta.scrollHeight, 96) + "px"
  }

  function submit() {
    const t = text.trim()
    if (!t || disabled) return
    onSubmit(t)
    setText("")
    if (taRef.current) {
      taRef.current.style.height = "auto"
      // Keep the input focused after sending so the customer can keep
      // typing without re-clicking. The textarea stays enabled while
      // Pearl responds (only the send button disables), so focus holds.
      taRef.current.focus()
    }
  }

  function handleKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  }

  const canSend = text.trim().length > 0 && !disabled

  return (
    <div className={styles["composer-pill"]}>
      <textarea
        ref={taRef}
        rows={1}
        className={styles.composer}
        placeholder={placeholder}
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          autoResize()
        }}
        onKeyDown={handleKey}
      />
      <button
        type="button"
        className={styles["send-btn"]}
        onClick={submit}
        disabled={!canSend}
        aria-label="Send"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 12h14M13 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  )
}
